import {
  ReportFeedIssueRequest,
  UrlCompanyFields,
  UrlDiscoveryResult,
} from '../../types/contentCompletion';

export const URL_FIELD_LABELS: Record<keyof UrlCompanyFields, string> = {
  website: 'Website',
  press_release_url: 'Press release page',
  feed_news_rss: 'News RSS',
};

export const DISPLAY_URL_FIELDS: (keyof UrlCompanyFields)[] = [
  'website',
  'press_release_url',
  'feed_news_rss',
];

export const formatUrlFieldValue = (value?: string | null): string => value || '—';

export type UrlDraftFields = Record<keyof UrlCompanyFields, string>;

const normalizeUrlDraftValue = (value?: string | null): string => (value ?? '').trim();

/** Seed editable drafts: prefer proposed, else current. */
export const buildUrlDraftFromDiscovery = (result: UrlDiscoveryResult): UrlDraftFields => {
  const draft = {} as UrlDraftFields;
  for (const field of DISPLAY_URL_FIELDS) {
    draft[field] =
      normalizeUrlDraftValue(result.proposed[field]) ||
      normalizeUrlDraftValue(result.current[field]);
  }
  return draft;
};

export const normalizeUrlForSave = (value: string): string | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

/** Fields where draft differs from current (empty draft clears a stored URL). */
export const getChangedUrlFields = (
  current: UrlCompanyFields,
  draft: UrlDraftFields
): (keyof UrlCompanyFields)[] =>
  DISPLAY_URL_FIELDS.filter((field) => {
    const currentNorm = normalizeUrlDraftValue(current[field]);
    const draftNorm = normalizeUrlDraftValue(draft[field]);
    return currentNorm !== draftNorm;
  });

export const draftHasUrlChanges = (
  current: UrlCompanyFields,
  draft: UrlDraftFields
): boolean => getChangedUrlFields(current, draft).length > 0;

export const buildUrlUpdatePayloadFromDraft = (
  current: UrlCompanyFields,
  draft: UrlDraftFields
): Partial<Record<keyof UrlCompanyFields, string | null>> => {
  const payload: Partial<Record<keyof UrlCompanyFields, string | null>> = {};
  for (const field of getChangedUrlFields(current, draft)) {
    payload[field] = normalizeUrlForSave(draft[field]);
  }
  return payload;
};

export const getDiscoveryResultSummary = (result: UrlDiscoveryResult): string => {
  if (result.summary) return result.summary;
  if (result.error) return result.error;
  if (result.warnings[0]) return result.warnings[0];
  return 'URL discovery complete';
};

export const URL_DISCOVERY_REPORTABLE_STATUSES = new Set([
  'website_not_found',
  'broken_no_replacement',
  'error',
]);

export const shouldReportUrlDiscoveryIssue = (result: UrlDiscoveryResult): boolean =>
  result.status === 'failure' || URL_DISCOVERY_REPORTABLE_STATUSES.has(result.check_status);

export const getDiscoveryIssueSourceUrl = (result: UrlDiscoveryResult): string => {
  if (result.existing_url) return result.existing_url;
  if (result.current.website) return result.current.website;
  if (result.proposed.website) return result.proposed.website;
  if (result.proposed.press_release_url) return result.proposed.press_release_url;
  if (result.proposed.feed_news_rss) return result.proposed.feed_news_rss;
  return '(no url)';
};

export const buildUrlDiscoveryIssueRequest = (
  companyId: string,
  companyName: string,
  result: UrlDiscoveryResult,
  error?: string
): ReportFeedIssueRequest => ({
  company_id: companyId,
  company_name: companyName,
  source_url: getDiscoveryIssueSourceUrl(result),
  issue_type:
    result.status === 'failure' ? 'discovery_error' : result.check_status || 'discovery_error',
  note: result.summary || error || result.warnings[0],
});

export const buildRejectedUrlDiscoveryReportRequest = (
  companyId: string,
  companyName: string,
  result: UrlDiscoveryResult
): ReportFeedIssueRequest => {
  const proposedParts = result.apply_fields
    .map((field) => {
      const value = result.proposed[field as keyof UrlCompanyFields];
      return value ? `${field.replace(/_/g, ' ')}: ${value}` : null;
    })
    .filter(Boolean);

  const sourceUrl =
    result.proposed.press_release_url ||
    result.proposed.feed_news_rss ||
    result.proposed.website ||
    getDiscoveryIssueSourceUrl(result);

  return {
    company_id: companyId,
    company_name: companyName,
    source_url: sourceUrl,
    issue_type: 'rejected_match',
    note:
      proposedParts.length > 0
        ? `User rejected proposed URLs: ${proposedParts.join(' · ')}`
        : 'User rejected proposed URL discovery result',
  };
};

export const NEEDS_REVIEW_ISSUE_TYPE = 'needs_review';

/** True when neither press-release page nor RSS is present (draft or discovery result). */
export const discoveryMissingPressSources = (
  result: UrlDiscoveryResult,
  draft?: UrlDraftFields
): boolean => {
  const press =
    normalizeUrlDraftValue(draft?.press_release_url) ||
    normalizeUrlDraftValue(result.proposed.press_release_url) ||
    normalizeUrlDraftValue(result.current.press_release_url);
  const rss =
    normalizeUrlDraftValue(draft?.feed_news_rss) ||
    normalizeUrlDraftValue(result.proposed.feed_news_rss) ||
    normalizeUrlDraftValue(result.current.feed_news_rss);
  return !press && !rss;
};

/** Flag a company for follow-up (incomplete discovery, possibly inactive, etc.). */
export const buildNeedsReviewReportRequest = (
  companyId: string,
  companyName: string,
  result: UrlDiscoveryResult,
  draft?: UrlDraftFields
): ReportFeedIssueRequest => {
  const website =
    normalizeUrlDraftValue(draft?.website) ||
    normalizeUrlDraftValue(result.proposed.website) ||
    normalizeUrlDraftValue(result.current.website);
  const missingPress = discoveryMissingPressSources(result, draft);
  const noteParts = [
    'Flagged for manual review from URL discovery.',
    missingPress ? 'No press release page or RSS found.' : null,
    website ? `Website: ${website}` : 'No website URL.',
    result.summary || null,
  ].filter(Boolean);

  return {
    company_id: companyId,
    company_name: companyName,
    source_url: website || getDiscoveryIssueSourceUrl(result),
    issue_type: NEEDS_REVIEW_ISSUE_TYPE,
    note: noteParts.join(' '),
  };
};
